import { DEVICE, getDeviceType } from 'utils/devices';

// Reusable "pick the right image for this device" helper. Media objects from the
// API can carry an `image-versions` array of pre-resized variants alongside the
// full-size original (`asset-url` + `width`/`height`), e.g.:
//
//   media['image-versions'] = [
//     { url: '..._iphone5s.png', width: 640,  height: 1061 },
//     { url: '..._iphone7.png',  width: 1242, height: 2060 },
//     { url: '..._ipad.png',     width: 768,  height: 1274 },
//     { url: '..._ipad_retina.png', width: 1536, height: 2548 },
//     { url: '..._desktop.png',  width: 1200, height: 1991 },
//   ]
//
// Supplying the smallest sufficient variant to consumers like <ReactAvatarEditor>
// avoids downloading and processing the huge original on small screens.

// Which variant (by filename token) to prefer per device bucket. Extend this map
// when new variants/devices are added — no consumer code needs to change.
export const DEVICE_VARIANT = {
  [DEVICE.MOBILE]: 'iphone5s',
  [DEVICE.TABLET]: 'ipad',
  [DEVICE.DESKTOP]: 'desktop',
};

const getVersions = (media) => {
  const versions = media && media['image-versions'];
  return Array.isArray(versions) ? versions : [];
};

// Match a URL whose filename ends with `_<token>.<ext>` (optionally followed by a
// query/hash on signed URLs). Anchored on the extension so `ipad` does NOT also
// match `ipad_retina`.
const matchesToken = (url, token) =>
  typeof url === 'string' && new RegExp(`_${token}\\.[a-z0-9]+([?#].*)?$`, 'i').test(url);

const toResult = (obj) => ({
  url: (obj && obj.url) || null,
  width: (obj && obj.width) || null,
  height: (obj && obj.height) || null,
});

const original = (media) => ({
  url: (media && (media['asset-url'] || media.url)) || null,
  width: (media && media.width) || null,
  height: (media && media.height) || null,
});

/**
 * Return the most appropriate image `{ url, width, height }` for the current
 * device, falling back to the original asset when no suitable variant exists.
 *
 * @param {object} media  Media object with `image-versions` and/or `asset-url`.
 * @param {object} [options]
 * @param {string} [options.deviceType]  Override the detected device bucket
 *                                       (one of DEVICE.*). Defaults to getDeviceType().
 * @param {object} [options.variantMap]  Override the device→token mapping.
 * @returns {{ url: string|null, width: number|null, height: number|null }}
 */
export const getBestImageVersion = (media, options = {}) => {
  const { deviceType = getDeviceType(), variantMap = DEVICE_VARIANT } = options;

  const versions = getVersions(media);
  if (!versions.length) return original(media);

  const token = variantMap[deviceType];
  const match = token && versions.find((v) => v && matchesToken(v.url, token));
  if (match && match.url) return toResult(match);

  // Variant array present but no match for this device → use the original.
  return original(media);
};

export default getBestImageVersion;
